import { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getMe, setAuthToken, bulkSyncFavorites } from '@/lib/api';
import { stopGuestMode } from '@/lib/guestMode';
import { getFavorites as getGuestFavorites, clearFavorites as clearGuestFavorites } from '@/lib/guestFavorites';

const AuthContext = createContext(null);

/**
 * Lift any localStorage guest favourites onto the newly-authenticated user's
 * backend-persisted favourites collection. Idempotent by design — the server
 * dedupes by (type, slug). We only clear the local cache after a successful
 * sync so a network blip doesn't lose the user's picks.
 */
async function syncGuestFavoritesToServer() {
  const guestFavs = getGuestFavorites();
  if (!guestFavs.length) return 0;
  const payload = guestFavs.map((f) => ({ type: f.type, slug: f.slug }));
  try {
    const res = await bulkSyncFavorites(payload);
    const synced = res?.data?.synced ?? 0;
    clearGuestFavorites();
    window.dispatchEvent(new CustomEvent('iariya:favorites-synced', { detail: { synced } }));
    return synced;
  } catch {
    // Keep the localStorage copy so a retry on next load is possible
    return 0;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('iariya_token');
    if (token) {
      setAuthToken(token);
      getMe()
        .then(async (res) => {
          setUser(res.data);
          // Lift any guest favourites the moment we confirm the session
          const synced = await syncGuestFavoritesToServer();
          if (synced > 0) {
            toast.success(`${synced} saved favourite${synced === 1 ? '' : 's'} moved to your dashboard`);
          }
        })
        .catch(() => {
          localStorage.removeItem('iariya_token');
          setAuthToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('iariya_token', token);
    setAuthToken(token);
    setUser(userData);
    stopGuestMode();  // clear any lingering guest-mode flag on sign-in
    // Lift any guest favourites onto the server (fire-and-forget)
    syncGuestFavoritesToServer().then((synced) => {
      if (synced > 0) {
        toast.success(`${synced} saved favourite${synced === 1 ? '' : 's'} moved to your dashboard`);
      }
    });
  };

  // Same as `login` but used by social-login flows where the token came from
  // an OAuth exchange (e.g. /api/auth/oauth/google/exchange) rather than the
  // username/password form. Identical effect today; kept as a separate name so
  // future provider-specific side-effects (analytics, welcome modals) have a
  // clean hook.
  const applyExternalLogin = async (token, userData) => login(token, userData);

  const refreshUser = async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('iariya_token');
    setAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, applyExternalLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
